import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { Menu, User, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { trackPageView } from '../api/systemLogs';

const PROFILE_REMINDER_KEY = 'profile_reminder_dismissed';

const isProfileComplete = (user) => {
  if (!user) return true;
  const name = (user.name || '').trim();
  const email = (user.email || '').trim();
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  // Customers: name + phone required (so we can contact them); admin/employee: name + email
  if ((user.role || '').toLowerCase() === 'customer') {
    const phone = (user.mobile_phone || user.phone_number || '').trim();
    return name.length >= 2 && validEmail && phone.length >= 5;
  }
  return name.length >= 2 && validEmail;
};

const DashboardLayout = ({ children, userRole = 'customer' }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showProfileReminder, setShowProfileReminder] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { portalName } = useBranding();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Show "Complete your profile" popup when profile is incomplete (once per session unless dismissed)
  useEffect(() => {
    if (!user) return;
    if (isProfileComplete(user)) return;
    if (sessionStorage.getItem(PROFILE_REMINDER_KEY)) return;
    setShowProfileReminder(true);
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;
    trackPageView(location.pathname).catch(() => {});
  }, [location.pathname, user?.id]);

  const handleDismissReminder = () => {
    sessionStorage.setItem(PROFILE_REMINDER_KEY, 'true');
    setShowProfileReminder(false);
  };

  const handleGoToProfile = () => {
    sessionStorage.setItem(PROFILE_REMINDER_KEY, 'true');
    setShowProfileReminder(false);
    navigate('/profile');
  };

  const hideMainScrollbar = location.pathname.startsWith('/super-admin/tenants');
  const isTenantFormShell = /^\/super-admin\/tenants\/(new|[^/]+\/edit)$/.test(location.pathname);
  let mainScrollClasses = 'overflow-y-auto';
  if (isTenantFormShell) {
    mainScrollClasses = 'overflow-hidden';
  } else if (hideMainScrollbar) {
    mainScrollClasses = 'overflow-y-auto scrollbar-hide';
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-slate-900 overflow-hidden transition-colors duration-300 relative">
      {/* 1. Mobile-only top bar: Menu + title + avatar (desktop unchanged, compact height) */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-14 bg-transparent dark:bg-transparent border-none shadow-none z-30 flex items-center px-3 justify-between">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-200"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <span className="font-semibold text-gray-900 dark:text-white">{portalName}</span>
        <button
          type="button"
          onClick={() => navigate('/profile')}
          className="w-9 h-9 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center shrink-0 active:scale-95 transition-transform focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-900"
          aria-label="Open profile"
        >
          <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </span>
        </button>
      </div>

      {/* 2. Mobile-only overlay (desktop: no overlay) */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-sm transition-opacity"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* 3. SIDEBAR: drawer on mobile w-64 (matches Sidebar), no extra column; desktop: normal. */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-slate-900 transform transition-transform duration-300 ease-out md:relative md:translate-x-0 md:w-auto md:flex-shrink-0 md:min-h-full ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Sidebar userRole={userRole} onCloseMobile={sidebarOpen ? () => setSidebarOpen(false) : undefined} />
      </div>

      {/* SIDEBAR DIVIDER (desktop only – unchanged) */}
      <div className="hidden md:block w-px bg-gray-200 dark:bg-gray-700 flex-shrink-0" />

      {/* 4. MAIN CONTENT – min-w-0 so flex gives it remaining space; never collapses to blank */}
      <div className="flex-1 flex flex-col overflow-hidden w-full min-w-0 min-h-0 bg-transparent dark:bg-transparent">
        <main
          className={`flex-1 min-h-0 flex flex-col overflow-x-hidden pt-14 md:pt-0 scroll-smooth ${mainScrollClasses}`}
          role="main"
        >
          <div
            className={`w-full px-3 py-3 md:px-6 lg:px-8 md:py-4 bg-transparent ${
              isTenantFormShell ? 'flex flex-1 flex-col min-h-0 overflow-hidden' : 'min-h-full'
            }`}
          >
            {children}
          </div>
        </main>
      </div>

      {/* Complete Profile Reminder Modal */}
      {showProfileReminder && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-amber-100 dark:bg-amber-900/30">
                <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Complete your profile</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-6">
              Please complete your profile so that your ticket will be solved easily and so we can contact you as soon as possible.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={handleGoToProfile}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-500/25 transition-all"
              >
                <User className="w-5 h-5" />
                Complete profile
              </button>
              <button
                type="button"
                onClick={handleDismissReminder}
                className="flex-1 px-4 py-3 rounded-xl font-semibold bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600 transition-all"
              >
                Remind me later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardLayout;

